import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import Switch from '../../../../uiKit/Switch'
import LoaderSmall from '../../../../uiKit/loaders/loaderSmall'
import SetupEmailAutomationModal from './SetupEmailAutomationModal'
import EmailCredentialsForm from './EmailCredentialsForm'
import EmailFormatPopover from './EmailFormatPopover'
import HealthStatusBanner from './HealthStatusBanner'
import LogsTable from './logsTable'
import { getEmailConfig, updateEmailConfig, updateEmailConfigById } from '../../api/emailAutomation'
import { alertError } from '../../../../api'
import classes from './styles.module.scss'

const EmailAutomation = ({ activeBot }) => {
  const [loading, setLoading] = useState(true)
  const [enabled, setEnabled] = useState(false)
  const [hasConfig, setHasConfig] = useState(false)
  const [isSetupModalOpen, setIsSetupModalOpen] = useState(false)
  const [setupModalMode, setSetupModalMode] = useState('setup')
  const [credentials, setCredentials] = useState({ senderEmail: '', redirectionEmail: '', receiverEmail: '', id: '' })
  const [emailHealthStatus, setEmailHealthStatus] = useState('')

  const applyEmailConfigToState = config => {
    const isEmptyConfigObject =
      config && typeof config === 'object' && !Array.isArray(config) && Object.keys(config).length === 0

    if (!config || isEmptyConfigObject) {
      setHasConfig(false)
      setEnabled(false)
      setCredentials({ senderEmail: '', redirectionEmail: '', receiverEmail: '', id: '' })
      setEmailHealthStatus('')
      return
    }

    setHasConfig(true)

    const healthStatus = config.emailHealthStatus || ''
    const isHealthDown = healthStatus && healthStatus !== 'UP'

    setEnabled(isHealthDown ? false : Boolean(config.isActive))
    setCredentials({
      senderEmail: config.senderEmailId || '',
      redirectionEmail: config.redirectionEmailId || '',
      receiverEmail: config.receiverEmailId || '',
      id: config.id,
    })
    setEmailHealthStatus(healthStatus)
  }

  const receiverEmail = credentials.receiverEmail || activeBot?.receiverEmail || activeBot?.email || ''

  const refreshEmailConfig = async botId => {
    setLoading(true)
    try {
      const config = await getEmailConfig(botId)
      applyEmailConfigToState(config)
    } catch (e) {
      alertError(e?.message || 'Failed to fetch email config')
    } finally {
      setLoading(false)
    }
  }

  const updateEmailAutomation = async (botId, isActive, values = {}) => {
    const payload = {
      isActive,
      receiverEmailId: values.receiverEmail || credentials.receiverEmail || receiverEmail,
      redirectionEmailId: values.redirectionEmail || credentials.redirectionEmail || '',
      senderEmailId: values.senderEmail || credentials.senderEmail || '',
    }

    if (credentials.id) {
      await updateEmailConfigById(botId, Number(credentials.id), payload)
    } else {
      await updateEmailConfig(botId, payload)
    }

    await refreshEmailConfig(botId)
  }

  useEffect(() => {
    if (!activeBot || !activeBot.id) {
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)

    getEmailConfig(activeBot.id)
      .then(config => {
        if (cancelled) return
        applyEmailConfigToState(config)
      })
      .catch(() => null)
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [activeBot])

  const handleCloseSetupModal = () => {
    setIsSetupModalOpen(false)
  }

  const handleSaveSetupModal = data => {
    if (!activeBot || !activeBot.id) return

    const nextValues = {
      senderEmail: data?.senderEmail || '',
      redirectionEmail: data?.redirectionEmail || '',
      receiverEmail: data?.receiverEmail || '',
    }

    setIsSetupModalOpen(false)
    setLoading(true)

    updateEmailAutomation(activeBot.id, true, nextValues)
      .then(() => null)
      .catch(e => {
        alertError(e?.message || 'Failed to update email automation')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const handleEditCredentials = () => {
    setSetupModalMode('edit')
    setIsSetupModalOpen(true)
  }

  const handleToggle = async () => {
    if (!activeBot || !activeBot.id) return

    if (enabled) {
      try {
        await updateEmailAutomation(activeBot.id, false)
      } catch (e) {
        alertError(e?.message || 'Failed to update email automation')
      }
      return
    }

    if (hasConfig) {
      try {
        await updateEmailAutomation(activeBot.id, true)
      } catch (e) {
        alertError(e?.message || 'Failed to update email automation')
      }
      return
    }

    setSetupModalMode('setup')
    setIsSetupModalOpen(true)
  }

  const isHealthDown = hasConfig && emailHealthStatus && emailHealthStatus !== 'UP'

  return (
    <div>
      <div className={classes.container}>
        <div className={classes.headerContainer}>
          <div>
            <h2 className={classes.pageTitle}>Care Plan to CareBrain Email Automation</h2>
            <p className={classes.paragraph}>
              Add email credentials from which you will send service user care plans
            </p>
          </div>
        </div>

        {loading ? (
          <div className={classes.loaderContainer}>
            <LoaderSmall showLoader />
          </div>
        ) : (
          <>
            <div className={classes.card}>
              <div className={classes.switchContainer}>
                <div className={classes.iconContainer}>
                  <img src="/images/platform/emailIcon.svg" alt="email automation icon" />
                  <div className={classes.textContainer}>
                    <div className={classes.title}>
                      {enabled ? 'Disable automated email uploads' : 'Enable automated email uploads'}
                      <EmailFormatPopover activeBotId={activeBot?.id} />
                    </div>
                    <div className={classes.description}>
                      {!enabled
                        ? "Enable this to let the system automatically process and upload service user's files received from your care centre's email address."
                        : ''}
                    </div>
                  </div>
                </div>
                <Switch checked={enabled} onChange={handleToggle} disabled={Boolean(isHealthDown)} />
              </div>

              <HealthStatusBanner visible={Boolean(isHealthDown)} />

              {hasConfig && (
                <EmailCredentialsForm
                  senderEmail={credentials.senderEmail}
                  redirectionEmail={credentials.redirectionEmail}
                  receiverEmail={receiverEmail}
                  onEdit={handleEditCredentials}
                  enabled={enabled}
                  activeBotId={activeBot?.id}
                />
              )}
            </div>

            {hasConfig && <LogsTable />}
          </>
        )}

        <SetupEmailAutomationModal
          open={isSetupModalOpen}
          onClose={handleCloseSetupModal}
          onSave={handleSaveSetupModal}
          initialSenderEmail={setupModalMode === 'edit' ? credentials.senderEmail : ''}
          initialRedirectionEmail={setupModalMode === 'edit' ? credentials.redirectionEmail : ''}
          mode={setupModalMode}
          activeBotId={activeBot?.id}
        />
      </div>
    </div>
  )
}

EmailAutomation.propTypes = {
  activeBot: PropTypes.object,
}

const mapStateToProps = state => ({
  activeBot: state.activeBot,
})

export default connect(mapStateToProps)(EmailAutomation)
